/**
 * GoogleIntegrationInlinePanel
 *
 * Bloco reutilizável com TODAS as opções da integração Google Agenda:
 * conta conectada, toggles de sincronização/conta corporativa, última
 * sincronização, ações (conectar / desconectar / excluir) e diagnóstico.
 *
 * Extraído de `CalendarIntegrationsPanel` para poder ser renderizado
 * inline dentro do modal de criação/edição de calendário
 * (`CalendarFormDialog`), concentrando tudo em um só lugar.
 *
 * RELEASE:
 *   2026-08-04 v1.0.0 - Extração do corpo de CalendarIntegrationsPanel com
 *                       suporte a variante compacta (`compact`).
 */

import { useState, useEffect } from 'react';
import {
  Calendar,
  Check,
  Unplug,
  Loader2,
  Trash2,
  AlertTriangle,
  Building2,
  RefreshCw,
  FileSearch,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import googleCalendarIcon from '@/assets/google-calendar-icon.png';
import { useCalendarIntegration } from '@/hooks/useSchedulingEventTypes';
import { useGoogleCalendarConnect } from '@/hooks/useGoogleCalendarConnect';
import { toast } from 'sonner';
import { CalendarOAuthDiagnosticsSheet } from './CalendarOAuthDiagnosticsSheet';

interface GoogleIntegrationInlinePanelProps {
  /**
   * Variante compacta usada dentro do modal de calendário (espaçamentos e
   * tipografia menores). Padrão: false (painel completo de configurações).
   */
  compact?: boolean;
  /**
   * Quando true, captura ?error=/?success= do redirect OAuth e exibe toast.
   * Deve ficar ativo em apenas um ponto da tela para não duplicar toasts.
   */
  handleOAuthRedirect?: boolean;
}

export function GoogleIntegrationInlinePanel({
  compact = false,
  handleOAuthRedirect = true,
}: GoogleIntegrationInlinePanelProps) {
  const {
    integration,
    inactiveIntegrations,
    isConnected,
    isLoading,
    disconnect,
    isDisconnecting,
    deleteIntegration,
    isDeleting,
    updateSettings,
    isUpdatingSettings,
  } = useCalendarIntegration();

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [diagnosticsCid, setDiagnosticsCid] = useState<string | null>(null);

  // Captura ?error=/?success= do redirect OAuth e mostra toast com cid
  useEffect(() => {
    if (!handleOAuthRedirect) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    const successParam = params.get('success');
    const cidParam = params.get('cid');

    if (!errorParam && !successParam) return;

    const cidShort = cidParam ? cidParam.slice(0, 8).toUpperCase() : null;

    if (successParam === 'true') {
      const email = params.get('email');
      toast.success(`Google Calendar conectado${email ? `: ${email}` : ''}`);
    } else if (errorParam) {
      toast.error(
        `Falha ao conectar Google Calendar (${errorParam})${cidShort ? ` · #${cidShort}` : ''}`,
        {
          action: cidParam
            ? {
                label: 'Ver diagnóstico',
                onClick: () => {
                  setDiagnosticsCid(cidParam);
                  setDiagnosticsOpen(true);
                },
              }
            : undefined,
          duration: 10000,
        },
      );
    }

    // Limpa query string para evitar toast em refresh
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }, [handleOAuthRedirect]);

  /** Inicia OAuth2 com Google Calendar (hook compartilhado). */
  const { connect: handleConnect, isConnecting } = useGoogleCalendarConnect({
    onDiagnostics: (cid) => {
      setDiagnosticsCid(cid);
      setDiagnosticsOpen(true);
    },
  });

  const handleDisconnect = async () => {
    try { await disconnect(); } catch { /* hook trata toast */ }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await deleteIntegration(deleteConfirmId);
      setDeleteConfirmId(null);
    } catch {
      setDeleteConfirmId(null);
    }
  };

  // Classes ajustadas conforme a variante
  const cardPad = compact ? 'p-3' : 'p-4';
  const rootGap = compact ? 'space-y-3' : 'space-y-4';

  return (
    <div className={rootGap}>
      {/* Loading skeleton */}
      {isLoading && (
        <div className={`flex items-center gap-3 rounded-lg border border-border/50 ${cardPad}`}>
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-28" />
          </div>
        </div>
      )}

      {/* Integração ativa */}
      {!isLoading && isConnected && integration && (
        <>
          <div className={`flex items-center gap-3 rounded-lg border border-border/50 bg-muted/30 ${cardPad}`}>
            <div className={`flex items-center justify-center shrink-0 ${compact ? 'h-8 w-8' : 'h-10 w-10'}`}>
              <img
                src={googleCalendarIcon}
                alt="Google Calendar"
                className={compact ? 'h-5 w-5' : 'h-7 w-7'}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">Google Calendar</p>
              {integration.provider_email ? (
                <p className="text-xs text-muted-foreground truncate">{integration.provider_email}</p>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1 text-xs text-amber-600">
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        <span className="truncate">Email não identificado</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Reconecte o calendário para identificar a conta Google vinculada.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1 shrink-0">
              <Check className="h-3 w-3" /> Conectado
            </Badge>
          </div>

          {/* Preferências de sincronização */}
          <div className={`space-y-3 rounded-lg border border-border/50 ${cardPad}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <Label htmlFor="sync-enabled" className="text-sm font-medium flex items-center gap-2">
                  <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
                  Sincronização ativa
                </Label>
                <p className="text-xs text-muted-foreground">
                  Importa e envia eventos automaticamente entre o sistema e o Google Calendar.
                </p>
              </div>
              <Switch
                id="sync-enabled"
                checked={integration.sync_enabled ?? true}
                disabled={isUpdatingSettings}
                onCheckedChange={(checked) =>
                  updateSettings({ id: integration.id, sync_enabled: checked })
                }
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <Label htmlFor="is-corporate" className="text-sm font-medium flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                  Conta corporativa
                </Label>
                <p className="text-xs text-muted-foreground">
                  Marque se esta conta Google representa a empresa (não a pessoal).
                </p>
              </div>
              <Switch
                id="is-corporate"
                checked={integration.is_corporate ?? false}
                disabled={isUpdatingSettings}
                onCheckedChange={(checked) =>
                  updateSettings({ id: integration.id, is_corporate: checked })
                }
              />
            </div>

            {(integration.last_sync_at || integration.last_sync_error) && (
              <div className="pt-2 border-t border-border/50 text-xs">
                {integration.last_sync_error ? (
                  <p className="text-destructive flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span className="break-words">Erro: {integration.last_sync_error}</span>
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    Última sincronização:{' '}
                    {formatDistanceToNow(new Date(integration.last_sync_at), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Integrações inativas */}
      {!isLoading && inactiveIntegrations.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground font-medium">Calendários desconectados</p>
          {inactiveIntegrations.map((inactive: any) => (
            <div
              key={inactive.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-muted/20"
            >
              <div className="flex h-8 w-8 items-center justify-center shrink-0 opacity-50">
                <img src={googleCalendarIcon} alt="Google Calendar" className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground truncate">
                  {inactive.provider_email || inactive.calendar_id || 'primary'}
                </p>
              </div>
              <Badge variant="secondary" className="shrink-0 text-xs">Inativo</Badge>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setDeleteConfirmId(inactive.id)}
                disabled={isDeleting}
                title="Excluir permanentemente"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Estado vazio */}
      {!isLoading && !isConnected && inactiveIntegrations.length === 0 && (
        <div className={`text-center space-y-3 ${compact ? 'py-3' : 'py-6'}`}>
          {!compact && (
            <div className="flex justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted">
                <Calendar className="h-7 w-7 text-muted-foreground" />
              </div>
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-foreground">Nenhuma conta Google conectada</p>
            <p className="text-xs text-muted-foreground mt-1">
              Conecte sua conta Google para sincronizar os eventos automaticamente nos dois sentidos.
            </p>
          </div>
        </div>
      )}

      {/* Botões de ação */}
      {!isLoading && (
        <div className="flex gap-2">
          {isConnected ? (
            <>
              <Button
                variant="outline"
                className="flex-1 gap-2"
                onClick={handleDisconnect}
                disabled={isDisconnecting}
              >
                {isDisconnecting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Unplug className="h-4 w-4" />
                )}
                Desconectar
              </Button>
              <Button
                variant="outline"
                className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                onClick={() => setDeleteConfirmId(integration!.id)}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Excluir
              </Button>
            </>
          ) : (
            <Button onClick={handleConnect} className="w-full gap-2" disabled={isConnecting}>
              {isConnecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <img src={googleCalendarIcon} alt="" className="h-4 w-4" />
              )}
              Conectar conta Google
            </Button>
          )}
        </div>
      )}

      {/* Atalho de diagnóstico — sempre disponível */}
      {!isLoading && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs text-muted-foreground gap-2"
          onClick={() => {
            setDiagnosticsCid(null);
            setDiagnosticsOpen(true);
          }}
        >
          <FileSearch className="h-3.5 w-3.5" />
          Ver diagnóstico de conexões
        </Button>
      )}

      {/* Sheet de diagnóstico */}
      <CalendarOAuthDiagnosticsSheet
        open={diagnosticsOpen}
        onOpenChange={setDiagnosticsOpen}
        initialCorrelationId={diagnosticsCid}
      />

      {/* Confirmação de exclusão */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir integração permanentemente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A integração será removida permanentemente
              e qualquer calendário ou tipo de evento vinculado a ela perderá a associação.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmDelete}
            >
              Excluir permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
